import { useEffect } from 'react';

/*
  NOTE: We use direct DOM manipulation here because Bootstrap's Modal
  renders .modal-dialog outside the React tree (via portal).
  React refs cannot be used for this element.
  This approach is safe and scoped to modal positioning only.
*/

/**
 * Offset applied to the modal's top position to ensure proper alignment.
 * Default value is 50 pixels, but it can be customized via the `offset` parameter.
 */

const MODAL_POSITION_RETRY_DELAY = 100; // Delay in milliseconds
const MODAL_POSITION_INITIAL_DELAY = 10; // Initial delay for first attempt
const MODAL_POSITION_OFFSET = 50;

export function useModalPositioning(
  position: { top: number; left?: number } | null,
  show: boolean,
) {
  useEffect(() => {
    const timeoutIds: NodeJS.Timeout[] = []; // Array to store timeout IDs

    if (position && show) {
      const findAndPositionModal = () => {
        const modalDialog = document.querySelector(
          '.modal-dialog',
        ) as HTMLElement;

        if (modalDialog) {
          const adjustedTop = Math.max(0, position.top - MODAL_POSITION_OFFSET);
          modalDialog.style.top = `${adjustedTop}px`;
          return true;
        }
        return false;
      };

      const maxRetries = 5;
      let attempts = 0;

      const retryPositioning = () => {
        if (findAndPositionModal()) {
          return; // Success - positioning complete
        }

        attempts++;
        if (attempts < maxRetries) {
          const timeoutId = setTimeout(
            retryPositioning,
            MODAL_POSITION_RETRY_DELAY,
          );
          timeoutIds.push(timeoutId);
        } else {
          console.warn('Modal positioning failed after maximum retries.');
        }
      };

      // Try positioning immediately
      if (!findAndPositionModal()) {
        // If immediate positioning fails, start retry logic
        const initialTimeoutId = setTimeout(
          retryPositioning,
          MODAL_POSITION_INITIAL_DELAY,
        );
        timeoutIds.push(initialTimeoutId);
      }
    }

    // Cleanup function - clear all timeouts when component unmounts or dependencies change
    return () => {
      timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, [position, show]);
}
